import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ContingentRecord, MedicalRecord, ReferralRecord } from '@/entities/medical/model/medicalSchema';
import type { Status } from '@/entities/user/model/userSchema';
import { mockDelay } from '@/shared/api/mockDelay';
import { appendJsonArray, readJsonArray } from '@/shared/api/queryStorage';
import { buildMedicalRecordDetail, medicalRecords, contingentRecords, referralRecords } from './mockData';

const MED_EXTRA_KEY = 'ohs-arm-medical-extra';
const MED_RESULT_PATCHES_KEY = 'ohs-arm-medical-result-patches';
const CONTINGENT_EXTRA_KEY = 'ohs-arm-contingent-extra';
const CONTINGENT_HISTORY_KEY = 'ohs-arm-contingent-history';
const REFERRAL_EXTRA_KEY = 'ohs-arm-referral-extra';

function referralPairKey(name: string, position: string): string {
  return `${name.trim()}|${position.trim()}`;
}

function mergeMedicalRecordsFromStorage(): MedicalRecord[] {
  const extras = readJsonArray<MedicalRecord>(MED_EXTRA_KEY);
  const patches = readJsonArray<{
    id: number;
    result: string;
    attachmentName?: string;
  }>(MED_RESULT_PATCHES_KEY);
  const base = [...medicalRecords, ...extras];
  const patchMap = new Map(patches.map((p) => [p.id, p]));
  return base.map((r) => {
    const patch = patchMap.get(r.id);
    if (!patch) return r;
    return {
      ...r,
      result: patch.result,
      attachmentName: patch.attachmentName,
      status: (patch.result === 'Не допущен' ? 'err' : 'ok') as Status,
    };
  });
}

/** GET /api/medical/records */
export function useMedicalRecords() {
  return useQuery({
    queryKey: ['medical', 'records'],
    queryFn: () => mockDelay(mergeMedicalRecordsFromStorage()),
  });
}

/** GET /api/medical/records/:id — карточка медосмотра */
export function useMedicalRecord(id: number) {
  return useQuery({
    queryKey: ['medical', 'records', 'detail', id],
    queryFn: async () => {
      const merged = mergeMedicalRecordsFromStorage();
      const record = merged.find((r) => r.id === id);
      if (!record) return null;
      return mockDelay(buildMedicalRecordDetail(record));
    },
    enabled: Number.isFinite(id) && id > 0,
  });
}

/** POST /api/medical/records */
export function useAddMedicalRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Omit<MedicalRecord, 'id'>) => {
      const id = Date.now();
      const row: MedicalRecord = { id, ...input };
      appendJsonArray(MED_EXTRA_KEY, row);
      await mockDelay(null);
      return row;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['medical', 'records'] });
    },
  });
}

/** PATCH /api/medical/records/:id/result */
export function useUpdateMedicalResult() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: number; result: string; attachmentName?: string }) => {
      appendJsonArray(MED_RESULT_PATCHES_KEY, input);
      await mockDelay(null);
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['medical', 'records'] });
      void queryClient.invalidateQueries({ queryKey: ['medical', 'records', 'detail', variables.id] });
    },
  });
}

/** GET /api/medical/contingent */
export function useContingentRecords() {
  return useQuery({
    queryKey: ['medical', 'contingent'],
    queryFn: () => mockDelay([...contingentRecords, ...readJsonArray<ContingentRecord>(CONTINGENT_EXTRA_KEY)]),
  });
}

/** GET /api/medical/contingent/history */
export function useContingentHistory() {
  return useQuery({
    queryKey: ['medical', 'contingent', 'history'],
    queryFn: () => mockDelay(readJsonArray<{ date: string; action: string }>(CONTINGENT_HISTORY_KEY)),
  });
}

/** POST /api/medical/contingent */
export function useAddContingentRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Omit<ContingentRecord, 'id'>) => {
      const id = Date.now();
      const row: ContingentRecord = { id, ...input };
      appendJsonArray(CONTINGENT_EXTRA_KEY, row);
      const historyEntry = {
        date: new Date().toLocaleString('ru-RU'),
        action: `Добавлена позиция: ${row.position} (${row.department})`,
      };
      appendJsonArray(CONTINGENT_HISTORY_KEY, historyEntry);
      await mockDelay(null);
      return row;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['medical', 'contingent'] });
      void queryClient.invalidateQueries({
        queryKey: ['medical', 'contingent', 'history'],
      });
    },
  });
}

/** GET /api/medical/referrals */
export function useReferralRecords() {
  return useQuery({
    queryKey: ['medical', 'referrals'],
    queryFn: () => mockDelay([...referralRecords, ...readJsonArray<ReferralRecord>(REFERRAL_EXTRA_KEY)]),
  });
}

/** POST /api/medical/referrals/generate */
export function useGenerateReferrals() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (refs: Omit<ReferralRecord, 'id'>[]) => {
      const existing = readJsonArray<ReferralRecord>(REFERRAL_EXTRA_KEY);
      const knownKeys = new Set<string>([
        ...referralRecords.map((r) => referralPairKey(r.employeeName, r.position)),
        ...existing.map((r) => referralPairKey(r.employeeName, r.position)),
      ]);
      const toAdd = refs.filter((r) => !knownKeys.has(referralPairKey(r.employeeName, r.position)));
      if (!toAdd.length) {
        await mockDelay(null);
        return [];
      }
      const startId = Date.now();
      const rows: ReferralRecord[] = toAdd.map((r, i) => ({
        id: startId + i,
        ...r,
      }));
      localStorage.setItem(REFERRAL_EXTRA_KEY, JSON.stringify([...existing, ...rows]));
      await mockDelay(null);
      return rows;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['medical', 'referrals'] });
    },
  });
}
